import { AppSnackbarService } from '@aca-new/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { EmailsValidators } from '@aca-new/app/shared/validators/emails.validators';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { TranslateService } from '@ngx-translate/core';
import { EQimaAvatarSize, EQimaSnackbarType, qimaIsOptionalTrueStringBoolean, QimaOptionalType, QimaSimpleChangesType, QimaStringBooleanType } from '@qima/ngx-qima';
import { has, isFunction, noop } from 'lodash/index';

@Component({
  standalone: false,
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef((): typeof EmailComponent => EmailComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef((): typeof EmailComponent => EmailComponent),
      multi: true,
    },
  ],
})
export class EmailComponent extends ControlValueAccessor<string> implements OnChanges, Validator {
  /**
   * @description
   * Define the placeholder of the email input
   * @type {string}
   * @default ''
   */
  @Input('emailPlaceholder')
  public placeholder: string = '';

  /**
   * @description
   * Change the UI to either green or red based on the user interactions (dirty, unfocused and touched state)
   * This can be very useful if you display a page that needs form validation
   * If you wish to display on the first rendering the validation state,
   * You can simply mark the control as dirty and touched
   * @example
   * formControl.markAsDirty();
   * formControl.markAsTouched();
   * @type {QimaOptionalType<QimaStringBooleanType>}
   * @default false
   */
  @Input('emailHasValidators')
  public __hasValidators: QimaOptionalType<QimaStringBooleanType> = undefined;

  /**
   * @description
   * The value is required or optional
   * @type {QimaOptionalType<QimaStringBooleanType>}
   * @default false
   */
  @Input('emailIsRequired')
  public __isRequired: QimaOptionalType<QimaStringBooleanType> = undefined;

  /**
   * @description
   * Disable or enable the input
   * @type {QimaOptionalType<QimaStringBooleanType>}
   * @default false
   */
  @Input('emailIsDisabled')
  public __isDisabled: QimaOptionalType<QimaStringBooleanType> = undefined;

  /**
   * @description
   * Locked recipient chip labels that are always shown and cannot be removed by default.
   * Not included in the CVA value. When present, they satisfy required validation.
   * @type {ReadonlyArray<string>}
   * @default []
   */
  @Input('emailLockedLabels')
  public lockedLabels: readonly string[] = [];

  /**
   * @description
   * When true, locked label chips show a close button and can be removed like other emails.
   * Removing a locked label emits emailLockedLabelRemove; the parent must update emailLockedLabels.
   * @type {QimaOptionalType<QimaStringBooleanType>}
   * @default false
   */
  @Input('emailIsLockedLabelsRemovable')
  public __isLockedLabelsRemovable: QimaOptionalType<QimaStringBooleanType> = undefined;

  /**
   * @description
   * Emits the locked label when the user removes it (only when emailIsLockedLabelsRemovable is true).
   * @type {EventEmitter<string>}
   */
  @Output('emailLockedLabelRemove')
  public readonly lockedLabelRemove: EventEmitter<string> = new EventEmitter<string>();

  public value: string = '';
  public emails: FormControl<string[]> = new FormControl<string[]>();
  public hasValidatorsState: boolean = false;
  public isRequiredState: boolean = false;
  public isDisabledState: boolean = false;
  public isLockedLabelsRemovableState: boolean = false;
  public readonly avatarSize: typeof EQimaAvatarSize = EQimaAvatarSize;

  private _onValidatorChange: () => void = noop;

  public constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appSnackbarService: AppSnackbarService,
    private readonly _translateService: TranslateService
  ) {
    super();
  }

  public ngOnChanges(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    this._onHasValidatorsChange(changes);
    this._onIsRequiredChange(changes);
    this._onIsDisabledChange(changes);
    this._onIsLockedLabelsRemovableChange(changes);
    this._onLockedLabelsChange(changes);
    this._syncEmailsValidators();
    this._changeDetectorRef.markForCheck();
  }

  public writeValue(value: Readonly<string>): void {
    this.emails = new FormControl<string[]>(value ? value.replace(/[,|;]/g, ' ').trim().split(' ') : []);
    this.emails.markAsDirty();
    this._changeDetectorRef.detectChanges();
    this._syncEmailsValidators();
  }

  public registerOnTouched(fn: () => void): void {
    super.registerOnTouched((): void => {
      fn();
    });
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    const raw = control.value;
    const isEmpty = raw == null || raw === '';

    if (this.isRequiredState && isEmpty && this.lockedLabels.length === 0) {
      return { required: true };
    }

    if (!this.hasValidatorsState || isEmpty) {
      return null;
    }

    return EmailsValidators.emails(control);
  }

  public registerOnValidatorChange(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public onEmailsChange(emails: Readonly<string[]>): void {
    this.value = emails.join(';');

    if (isFunction(this.onChange)) {
      this.onChange(this.value ?? null);
    }
  }

  public acceptEmail(value: Readonly<string>): boolean {
    return !this.value.includes(value);
  }

  public valueRefused(value: Readonly<string>): void {
    if (!value || value.length === 0) {
      return;
    }

    return this._appSnackbarService.showSnackbar(this._translateService.instant('COMMON.EMAIL_DUPLICATION', { email: value }) as string, EQimaSnackbarType.WARNING);
  }

  /**
   * Emits when a removable locked label chip is closed.
   * @param {string} label - Locked label removed by the user.
   * @returns {void} Nothing.
   */
  public onLockedLabelRemove(label: string): void {
    if (!this.isLockedLabelsRemovableState || this.isDisabledState) {
      return;
    }

    this.lockedLabelRemove.emit(label);
  }

  private _onHasValidatorsChange(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    if (has(changes, '__hasValidators') && changes.__hasValidators.currentValue !== changes.__hasValidators.previousValue) {
      this.hasValidatorsState = qimaIsOptionalTrueStringBoolean(changes.__hasValidators.currentValue);
      this._onValidatorChange();
    }
  }

  private _onIsRequiredChange(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    if (has(changes, '__isRequired') && changes.__isRequired.currentValue !== changes.__isRequired.previousValue) {
      this.isRequiredState = qimaIsOptionalTrueStringBoolean(changes.__isRequired.currentValue);
      this._onValidatorChange();
    }
  }

  private _onIsDisabledChange(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    if (has(changes, '__isDisabled') && changes.__isDisabled.currentValue !== changes.__isDisabled.previousValue) {
      this.isDisabledState = qimaIsOptionalTrueStringBoolean(changes.__isDisabled.currentValue);
    }
  }

  private _onIsLockedLabelsRemovableChange(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    if (has(changes, '__isLockedLabelsRemovable') && changes.__isLockedLabelsRemovable.currentValue !== changes.__isLockedLabelsRemovable.previousValue) {
      this.isLockedLabelsRemovableState = qimaIsOptionalTrueStringBoolean(changes.__isLockedLabelsRemovable.currentValue);
    }
  }

  private _onLockedLabelsChange(changes: Readonly<QimaSimpleChangesType<EmailComponent>>): void {
    if (has(changes, 'lockedLabels') && changes.lockedLabels.currentValue !== changes.lockedLabels.previousValue) {
      this._onValidatorChange();
    }
  }

  /**
   * Keeps the inner chip input validity aligned with the current required / locked label state.
   * @returns {void} Nothing.
   */
  private _syncEmailsValidators(): void {
    const validators: ValidatorFn[] = [];

    if (this.isRequiredState && this.lockedLabels.length === 0) {
      validators.push(Validators.required);
    }

    if (this.hasValidatorsState) {
      validators.push(EmailsValidators.emails);
    }

    this.emails.setValidators(validators);
    this.emails.updateValueAndValidity();
  }
}
